import React, { useRef, useState } from "react";
import { Button, Checkbox, Divider, Form, Input, InputRef, Modal, Select, Space, Tooltip, message } from "antd";
import { PlusOutlined, QuestionCircleTwoTone } from "@ant-design/icons";
// eslint-disable-next-line camelcase
import * as All_EPSG from "epsg-index/all.json";
import { ControlPointsImportData, ProjectRow } from "../../../types/interfaces";
import { makeid } from "../../../common/utils";

interface ControlPointsImportModalProps {
    onCancel: () => void;
    projects: ProjectRow[];
    fields: string[];
    callBack: (values: ControlPointsImportData, projects: ProjectRow[]) => void;
}

export const ControlPointsImportModal = ({ onCancel, projects, fields, callBack }: ControlPointsImportModalProps) => {
    const [projectList, setProjectList] = useState([...projects]);
    const [fieldList] = useState(["", ...fields]);
    const [projectToAdd, setProjectToAdd] = useState("");

    const inputRef = useRef<InputRef>(null);

    const handleCancel = () => {
        onCancel();
    };

    const onProjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setProjectToAdd(event.target.value);
    };

    const addItem = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        if (!projectToAdd) return;
        e.preventDefault();

        const projectId = makeid(5);
        setProjectList([...projectList, { id: projectId, project: projectToAdd }]);
        setProjectToAdd("");
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const onFinish = (values: ControlPointsImportData) => {
        if (!values.projectId || !values.epsg) {
            message.warning("Please put project and EPSG.", 2);
        } else if (!values.x || !values.y || !values.z) {
            message.warning("Please select X, Y, Z fields.", 2);
        } else {
            const projIndex = projectList.findIndex((p) => p.id === values.projectId);
            if (projIndex > -1) {
                const temp = [...projectList];

                const projItem = temp[projIndex];
                temp[projIndex] = { ...projItem, epsg: values.epsg };
                callBack(values, temp);
            }
        }
    };

    return (
        <Modal
            open
            className="control-points-import-modal"
            title="Import"
            width={450}
            onCancel={handleCancel}
            bodyStyle={{ backgroundColor: "transparent" }}
            footer={null}
        >
            <Form name="control_points_import" onFinish={onFinish} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                <div className="input-content" style={{ overflow: "auto", height: 430, marginBottom: 10 }}>
                    <Form.Item name="projectId" label="Project">
                        <Select
                            placeholder="custom dropdown render"
                            dropdownRender={(menu) => (
                                <>
                                    {menu}
                                    <Divider style={{ margin: "8px 0" }} />
                                    <Space style={{ padding: "0 8px 4px" }}>
                                        <Input
                                            placeholder="Please enter item"
                                            ref={inputRef}
                                            value={projectToAdd}
                                            onChange={onProjectChange}
                                        />
                                        <Button type="text" icon={<PlusOutlined />} onClick={addItem}>
                                            Add item
                                        </Button>
                                    </Space>
                                </>
                            )}
                            options={projectList.map((item) => ({ label: item.project, value: item.id }))}
                        />
                    </Form.Item>
                    <Form.Item name="epsg" label="EPSG">
                        <Select
                            showSearch
                            placeholder="Search to Select"
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.value ?? "").includes(input)}
                            filterSort={(optionA, optionB) =>
                                (optionA?.value ?? "").toLowerCase().localeCompare((optionB?.value ?? "").toLowerCase())
                            }
                            options={Object.entries(All_EPSG).map(([epsg, values]: [string, any]) => ({
                                label: (
                                    <div key={epsg} className="epsg-label">
                                        <span>{epsg}</span>
                                        <Tooltip
                                            placement="right"
                                            title={
                                                <div className="epsg-more">
                                                    {Object.keys(values).map((key) => {
                                                        if (key === "bbox" || key === "wkt") {
                                                            return <div key={key} />;
                                                        }
                                                        return (
                                                            <div key={key}>
                                                                <span>{key}:</span>
                                                                <span
                                                                    style={{
                                                                        color: "aqua",
                                                                        fontWeight: 600,
                                                                        marginLeft: 10
                                                                    }}
                                                                >
                                                                    {values[key] as string}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            }
                                            arrow
                                        >
                                            <Button
                                                shape="circle"
                                                icon={<QuestionCircleTwoTone />}
                                                style={{ border: "none" }}
                                            />
                                        </Tooltip>
                                    </div>
                                ),
                                value: epsg
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="hasHeader" label="Has header" valuePropName="checked">
                        <Checkbox />
                    </Form.Item>
                    <Form.Item name="title" label="Title">
                        <Select
                            placeholder="select title field"
                            options={fieldList.map((item) => ({ label: item, value: item }))}
                        />
                    </Form.Item>
                    <Form.Item name="x" label="X">
                        <Select
                            placeholder="select X field"
                            options={fieldList.map((item) => ({ label: item, value: item }))}
                        />
                    </Form.Item>
                    <Form.Item name="y" label="Y">
                        <Select
                            placeholder="select Y field"
                            options={fieldList.map((item) => ({ label: item, value: item }))}
                        />
                    </Form.Item>
                    <Form.Item name="z" label="Z">
                        <Select
                            placeholder="select z field"
                            options={fieldList.map((item) => ({ label: item, value: item }))}
                        />
                    </Form.Item>
                </div>

                <Form.Item style={{ textAlign: "right" }} wrapperCol={{ offset: 8 }}>
                    <Button type="primary" htmlType="submit">
                        Import
                    </Button>
                    <Button key="cancel" onClick={handleCancel} style={{ marginLeft: 10 }}>
                        Cancel
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};
